import { useTranslation } from 'react-i18next';
import type { ContractStatement } from '@mnc/contracts';
import { pick } from '../../i18n';
import { Badge } from '../../components/ui';
import { InvoiceStatusBadge, VARIATION_TONE, clientName, fmtDate, useMoney, usesRetention } from './shared';

/**
 * The statement of account: the contract, what every issued invoice certified (previous cumulative, this invoice,
 * cumulative, deductions, VAT, net due) and the approved variation orders. The page prints as the document — the
 * figures are the server's, the same ones the Excel export carries.
 */
export function StatementView({ statement }: { statement: ContractStatement }) {
  const { t, i18n } = useTranslation();
  const c = statement.contract;
  const money = useMoney(c.currency);
  const f = c.figures;
  const retention = usesRetention(c);
  const line = (label: string, minor: number, strong?: boolean) => (
    <div className={`flex justify-between gap-6 py-0.5 ${strong ? 'border-t border-neutral-300 pt-1 font-semibold dark:border-neutral-700' : ''}`}>
      <span>{label}</span>
      <span className="tabular-nums" dir="ltr">{money(minor)}</span>
    </div>
  );
  return (
    <div className="card grid gap-4 p-4">
      <header className="grid gap-1 border-b border-neutral-200 pb-3 dark:border-neutral-800">
        <h2 className="text-lg font-semibold" dir="auto">{t('contracts.statement')} — {pick(i18n.language, c.title_en, c.title_ar)}</h2>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300">
          <span>{t('contracts.reference')}: <span dir="ltr">{c.reference}</span></span>
          <span dir="auto">{t('contracts.client')}: {clientName(c, i18n.language)}</span>
          <span dir="ltr">{fmtDate(c.start_date, i18n.language)} → {fmtDate(c.revised_end_date ?? c.end_date, i18n.language, t('contracts.openEnded'))}</span>
          <span>{t('contracts.currency')}: {c.currency}</span>
          <span>{t('contracts.asOf')}: {fmtDate(statement.as_of, i18n.language)}</span>
        </div>
      </header>

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="text-sm">
          <h3 className="label">{t('contracts.value')}</h3>
          {line(t('contracts.figures.originalValue'), f.original_value_minor)}
          {line(t('contracts.figures.variations'), f.variations_value_minor)}
          {line(t('contracts.figures.revisedValue'), f.revised_value_minor, true)}
          {line(`${t('contracts.figures.vat')} (${new Intl.NumberFormat(i18n.language).format(c.vat_percent)}%)`, f.revised_vat_minor)}
          {line(t('contracts.figures.revisedWithVat'), f.revised_with_vat_minor, true)}
          {line(t('contracts.figures.certified'), f.certified_minor)}
          {line(t('contracts.figures.remaining'), f.remaining_minor)}
        </div>
        <div className="text-sm">
          <h3 className="label">{t('contracts.figures.account')}</h3>
          {line(t('contracts.figures.advanceInvoiced'), f.advance_invoiced_minor)}
          {line(t('contracts.figures.advanceRecovered'), f.advance_recovered_minor)}
          {retention && line(t('contracts.figures.retentionHeld'), f.retention_held_minor)}
          {line(t('contracts.figures.vat'), f.vat_invoiced_minor)}
          {line(t('contracts.figures.netInvoiced'), f.net_invoiced_minor, true)}
          {line(t('contracts.figures.paid'), f.paid_minor)}
          {line(t('contracts.figures.outstanding'), f.outstanding_minor, true)}
        </div>
      </section>

      <section>
        <h3 className="label mb-1">{t('contracts.invoices')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="uppercase text-neutral-500">
                <th className="p-1 text-start">{t('contracts.invoice')}</th>
                <th className="p-1 text-start">{t('contracts.invoiceDate')}</th>
                <th className="p-1 text-start">{t('contracts.dueDate')}</th>
                <th className="p-1 text-end">{t('contracts.figures.previousCumulative')}</th>
                <th className="p-1 text-end">{t('contracts.thisInvoice')}</th>
                <th className="p-1 text-end">{t('contracts.figures.cumulative')}</th>
                <th className="p-1 text-end">{t('contracts.figures.advanceRecovery')}</th>
                {retention && <th className="p-1 text-end">{t('contracts.figures.retention')}</th>}
                <th className="p-1 text-end">{t('contracts.figures.vat')}</th>
                <th className="p-1 text-end">{t('contracts.figures.netDue')}</th>
                <th className="p-1 text-end">{t('contracts.figures.paid')}</th>
                <th className="p-1 text-end">{t('contracts.figures.outstanding')}</th>
                <th className="p-1 text-start">{t('contracts.status')}</th>
              </tr>
            </thead>
            <tbody>
              {statement.invoices.map((i) => (
                <tr key={i.id} className="border-t border-neutral-200 dark:border-neutral-800">
                  <td className="p-1 tabular-nums" dir="ltr">#{i.number} <span className="text-neutral-500">{t(`contracts.invoiceKinds.${i.kind}`)}</span></td>
                  <td className="p-1" dir="ltr">{fmtDate(i.invoice_date, i18n.language)}</td>
                  <td className="p-1" dir="ltr">{fmtDate(i.due_date, i18n.language)}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.previous_gross_minor, { sign: false })}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.kind === 'progress' ? i.gross_minor : (i.amount_minor ?? i.gross_minor), { sign: false })}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.cumulative_gross_minor, { sign: false })}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.advance_recovery_minor, { sign: false })}</td>
                  {retention && <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.retention_minor, { sign: false })}</td>}
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.vat_minor, { sign: false })}</td>
                  <td className="p-1 text-end font-medium tabular-nums" dir="ltr">{money(i.net_due_minor, { sign: false })}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.paid_minor, { sign: false })}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(i.outstanding_minor, { sign: false })}</td>
                  <td className="p-1"><InvoiceStatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {statement.variations.length > 0 && (
        <section>
          <h3 className="label mb-1">{t('contracts.variations')}</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="uppercase text-neutral-500">
                <th className="p-1 text-start">{t('contracts.variation')}</th>
                <th className="p-1 text-start">{t('contracts.title')}</th>
                <th className="p-1 text-start">{t('contracts.decidedOn')}</th>
                <th className="p-1 text-end">{t('contracts.value')}</th>
                <th className="p-1 text-start">{t('contracts.status')}</th>
              </tr>
            </thead>
            <tbody>
              {statement.variations.map((v) => (
                <tr key={v.id} className="border-t border-neutral-200 dark:border-neutral-800">
                  <td className="p-1 tabular-nums" dir="ltr">#{v.number}</td>
                  <td className="p-1" dir="auto">{pick(i18n.language, v.title_en, v.title_ar)}</td>
                  <td className="p-1" dir="ltr">{fmtDate(v.decided_on, i18n.language)}</td>
                  <td className="p-1 text-end tabular-nums" dir="ltr">{money(v.value_minor, { sign: false })}</td>
                  <td className="p-1"><Badge tone={VARIATION_TONE[v.status]}>{t(`contracts.variationStatuses.${v.status}`)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <p className="text-xs text-neutral-500">{t('contracts.statementFooter')}</p>
    </div>
  );
}
